import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  sendResetVerificationEmail,
} from "../../../../redux/auth/action/userProfileActions";
import { resetTicketUpdate } from "../../../../redux/tickets/ticketActions";
import { VscUnverified, VscVerified } from "react-icons/vsc";
import ProfileUpdateModal from "./ProfileUpdateModal";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Shimmer from "../../../Layout/Shimmer";
import { 
  FaUser, 
  FaBriefcase, 
  FaMapMarkerAlt, 
  FaInfoCircle, 
  FaUpload, 
  FaCalendarCheck, 
  FaTicketAlt, 
  FaUserEdit, 
  FaLock, 
  FaChartLine,
  FaImage,
  FaBuilding,
  FaClock
} from "react-icons/fa";

const Profile = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const profileState = useSelector((state) => state.profile);
  const resetVerificationState = useSelector((state) => state.resetVerification);
  const eventsState = useSelector((state) => state.getOrganiserEvent);
  const ticketsState = useSelector((state) => state.tickets);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCooldown, setIsCooldown] = useState(false);
  const [hasDisplayedToast, setHasDisplayedToast] = useState(true);

  // Get organizer's events
  const organizerEvents = eventsState?.data?.results || [];
  const organizerEventIds = organizerEvents.map(event => event.event_id);

  // Get tickets data and filter by organizer's events
  const allTicketsArray = ticketsState?.tickets?.results || [];
  
  const organizerTickets = allTicketsArray.filter(ticket => 
    ticket.event_id && organizerEventIds.includes(ticket.event_id)
  );
  const totalTickets = organizerTickets.length;
  
  // Get upcoming events (next 7 days)
  const upcomingEvents = eventsState?.data?.results
    ?.filter(event => {
      if (!event.start_date) return false;
      const eventDate = new Date(event.start_date);
      const today = new Date();
      const nextWeek = new Date();
      nextWeek.setDate(today.getDate() + 7);
      return eventDate >= today && eventDate <= nextWeek;
    })
    .slice(0, 3) || [];

  useEffect(() => {
    if (
      resetVerificationState.data &&
      !resetVerificationState.loading &&
      !hasDisplayedToast
    ) {
      if (!toast.isActive(1, "profileComponent")) {
        toast.success("You've successfully requested an account verification email!", {
          toastId: 1
        });
      }
      setHasDisplayedToast(true);
    }
    if (
      resetVerificationState.error &&
      !hasDisplayedToast
    ) {
      if (!toast.isActive(2, "profileComponent")) {
        toast.error("There was a problem with sending a verification email. Please try again later.", {
          toastId: 2
        });
      }
      setHasDisplayedToast(true);
    }
  }, [
    resetVerificationState.data,
    resetVerificationState.error,
    resetVerificationState.loading,
    hasDisplayedToast,
  ]);

  useEffect(() => {
    if (!localStorage.getItem("access") && !sessionStorage.getItem("access")) {
      navigate("/login");
      dispatch(resetTicketUpdate());
    }
  }, [dispatch, navigate]);

  const { full_name, email, is_verified, company_name, description, business_email, business_phone, flat_house_number, street_address, city_town, county, company_logo, country, post_code } = profileState.profile || {};

  const safeValue = (value) => (value === null || value === undefined ? "" : value);

  const initialValuesFull = {
    full_name: safeValue(full_name),
    email: safeValue(email),
    phone_number: safeValue(profileState?.profile?.phone_number),
    company_name: safeValue(company_name),
    description: safeValue(description),
    business_email: safeValue(business_email),
    business_phone: safeValue(business_phone),
    flat_house_number: safeValue(flat_house_number),
    street_address: safeValue(street_address),
    city_town: safeValue(city_town),
    county: safeValue(county),
    post_code: safeValue(post_code),
    country: safeValue(country),
    company_logo: company_logo || "",
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleResendVerificationEmail = () => {
    if (email) {
      dispatch(sendResetVerificationEmail(email));
      setHasDisplayedToast(false);
      setIsCooldown(true);

      setTimeout(() => {
        setIsCooldown(false);
      }, 10000);
    }
  };

  // Format date for upcoming events
  const formatEventDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric'
    });
  };

  // Determine verification status inline
  let verificationContent;
  if (profileState.loading || !profileState.profile) {
    verificationContent = <Shimmer width="80px" height="24px" borderRadius="rounded-full" />;
  } else if (is_verified === true) {
    verificationContent = (
      <div className="flex items-center px-3 py-1 bg-green-50 rounded-full">
        <span className="text-green-600 text-sm font-medium">Verified</span>
        <VscVerified size={16} className="ms-1 text-green-600" />
      </div>
    );
  } else if (is_verified === false) {
    verificationContent = (
      <div className="flex flex-col items-center gap-1">
        <button
          onClick={handleResendVerificationEmail}
          disabled={isCooldown}
          title="Click to resend a verification link to your email"
          className={`flex items-center px-3 py-1 rounded-full ${isCooldown
            ? "bg-gray-50 cursor-not-allowed"
            : "bg-red-50 hover:bg-red-100"
            }`}
        >
          <span className={`text-sm font-medium ${isCooldown ? "text-gray-500" : "text-red-600"}`}>
            Unverified
          </span>
          <VscUnverified size={16} className="ms-1" />
        </button>
        {isCooldown && (
          <span className="text-red-500 text-xs">
            Wait 10s to resend
          </span>
        )}
      </div>
    );
  } else {
    verificationContent = <Shimmer width="80px" height="24px" borderRadius="rounded-full" />;
  }

  return (
    <div className="max-w-7xl mx-auto xl:p-5">
      <ToastContainer position="top-right" containerId="profileComponent" autoClose={3000} />

      {!profileState.profile && (
        <Shimmer width="100%" height="290px" borderRadius="rounded-lg" />
      )}

      {profileState.profile && (
        <>
          {/* Header Section */}
          <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
            <div className="flex flex-col md:flex-row gap-6">
              {/* Left Column: Profile Image and Verification */}
              <div className="flex flex-col items-center md:items-start gap-4">
                <div className="relative group">
                  {company_logo ? (
                    <div className="relative w-28 h-28 sm:w-36 sm:h-36 rounded-full overflow-hidden border-4 border-[#FF8100] shadow-md">
                      <img
                        src={company_logo}
                        alt="Profile"
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.src = "https://via.placeholder.com/150?text=Profile"; // Fallback image
                          e.target.onerror = null;
                        }}
                      />
                      <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all duration-200">
                        <FaImage className="text-white opacity-0 group-hover:opacity-100 transition-all duration-200" size={24} />
                      </div>
                    </div>
                  ) : (
                    <div className="relative w-28 h-28 sm:w-36 sm:h-36 rounded-full bg-gray-100 flex items-center justify-center border-4 border-[#FF8100] shadow-md">
                      <span className="text-3xl sm:text-4xl text-gray-400">
                        {full_name?.charAt(0)?.toUpperCase() || "?"}
                      </span>
                      <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all duration-200 rounded-full">
                        <FaImage className="text-white opacity-0 group-hover:opacity-100 transition-all duration-200" size={24} />
                      </div>
                    </div>
                  )}
                  <button 
                    onClick={() => setIsModalOpen(true)}
                    className="absolute -bottom-2 -right-2 bg-[#FF8100] text-white p-2 rounded-full shadow-md hover:bg-[#e67700] transition-all duration-200"
                    title="Update profile picture"
                  >
                    <FaUpload size={14} />
                  </button>
                </div>
                
                <div className="flex items-center justify-center">
                  {verificationContent}
                </div>
              </div>

              {/* Right Column: Profile Info and Buttons */}
              <div className="flex-grow">
                {/* Profile Info */}
                <div className="text-center md:text-left mb-5">
                  <h1 className="text-2xl font-bold text-gray-800 mb-1">{full_name}</h1>
                  <p className="text-gray-600 mb-2">{email}</p>
                  {company_name && (
                    <div className="flex items-center text-[#004B95] font-medium justify-center md:justify-start">
                      <FaBuilding className="mr-2" size={16} />
                      <p>{company_name}</p>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex flex-wrap justify-center md:justify-start gap-3">
                  <button
                    onClick={() => setIsModalOpen(true)}
                    className="px-4 py-2 bg-[#004B95] text-white text-sm rounded-lg hover:bg-[#00376f] transition-all duration-200 flex items-center shadow-sm"
                  >
                    <FaUserEdit className="mr-2" size={14} />
                    Update Profile
                  </button>
                  <button
                    onClick={() => navigate("/ForgotPassword")}
                    className="px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50 transition-all duration-200 flex items-center"
                  >
                    <FaLock className="mr-2" size={14} />
                    Change Password
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Stats and Upcoming Events Overview */}
          <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
            <h2 className="text-xl font-semibold mb-5 flex items-center">
              <FaChartLine className="mr-2 text-[#FF8100]" />
              Dashboard Overview
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Stats Card 1 - Made more compact */}
              <div className="bg-gradient-to-r from-[#FF8100] to-[#FFA94D] rounded-lg shadow-sm">
                <div className="p-4">
                  <div className="flex items-center">
                    <div className="mr-3">
                      <p className="text-white/90 text-xs font-medium">Total Events</p>
                      <h3 className="text-2xl font-bold text-white">{organizerEvents.length}</h3>
                    </div>
                    <div className="p-2 bg-white/20 rounded-lg ml-auto">
                      <FaCalendarCheck size={20} className="text-white" />
                    </div>
                  </div>
                  <button 
                    onClick={() => navigate("/dashboard?section=manage-events")}
                    className="mt-2 text-xs text-white/90 hover:text-white flex items-center"
                  >
                    View all events
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              </div>
              
              {/* Stats Card 2 - Made more compact */}
              <div className="bg-gradient-to-r from-[#0767c9] to-[#0875E1] rounded-lg shadow-sm">
                <div className="p-4">
                  <div className="flex items-center">
                    <div className="mr-3">
                      <p className="text-white/90 text-xs font-medium">Total Tickets</p>
                      <h3 className="text-2xl font-bold text-white">{totalTickets}</h3>
                    </div>
                    <div className="p-2 bg-white/20 rounded-lg ml-auto">
                      <FaTicketAlt size={20} className="text-white" />
                    </div>
                  </div>
                  <button 
                    onClick={() => navigate("/dashboard?section=purchase-logs")}
                    className="mt-2 text-xs text-white/90 hover:text-white flex items-center"
                  >
                    View orders
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              </div>
              
              {/* Upcoming Events Section - Fixed to properly display events in a grid */}
              <div className="md:col-span-1 lg:col-span-1 bg-gray-50 rounded-lg border border-gray-100 p-4">
                <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                  <FaCalendarCheck className="mr-2 text-[#FF8100]" size={14} />
                  Upcoming Events
                </h3>
                
                {upcomingEvents.length > 0 ? (
                  <div className="space-y-2">
                    {upcomingEvents.map((event, index) => (
                      <div 
                        key={index} 
                        onClick={() => navigate(`/editForm/${event.event_id}`)}
                        className="p-2 bg-white rounded border border-gray-100 hover:border-[#FF8100] hover:shadow-sm cursor-pointer transition-all duration-200"
                      >
                        <p className="font-medium text-sm text-gray-800 truncate">{event.event_name}</p>
                        <div className="flex items-center justify-between mt-1">
                          <span className="text-xs text-gray-500">{formatEventDate(event.start_date)}</span>
                          <span className="text-xs px-2 py-0.5 bg-[#FF8100]/10 text-[#FF8100] rounded-full">
                            {event.event_type}
                          </span>
                        </div>
                      </div>
                    ))}
                    <button 
                      onClick={() => navigate("/dashboard?section=manage-events")}
                      className="w-full mt-1 text-center text-xs text-[#FF8100] hover:text-[#e67700] transition-colors"
                    >
                      View all events →
                    </button>
                  </div>
                ) : (
                  <div className="text-center py-2">
                    <p className="text-gray-500 text-xs mb-2">No upcoming events</p>
                    <button 
                      onClick={() => navigate("/createEventForm")}
                      className="px-3 py-1 bg-[#FF8100] text-white text-xs rounded hover:bg-[#e67700] transition-colors"
                    >
                      Create Event
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Personal Information Card */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="text-xl font-semibold mb-4 flex items-center">
                <FaUser className="mr-2 text-[#004B95]" />
                Personal Information
              </h2>
              <div className="space-y-4">
                <div className="border-b border-gray-100 pb-3">
                  <label className="text-sm text-gray-500 block">Full Name</label>
                  <p className="font-medium break-words text-gray-800">{full_name}</p>
                </div>
                <div className="border-b border-gray-100 pb-3">
                  <label className="text-sm text-gray-500 block">Email</label>
                  <p className="font-medium break-words text-gray-800">{email}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500 block">Phone</label>
                  <p className="font-medium break-words text-gray-800">
                    {profileState?.profile?.phone_number || 'Not provided'}
                  </p>
                </div>
              </div>
            </div>

            {/* Business Information Card */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="text-xl font-semibold mb-4 flex items-center">
                <FaBriefcase className="mr-2 text-[#004B95]" />
                Business Details
              </h2>
              <div className="space-y-4">
                <div className="border-b border-gray-100 pb-3">
                  <label className="text-sm text-gray-500 block">Company Name</label>
                  <p className="font-medium break-words text-gray-800">{company_name || 'Not provided'}</p>
                </div>
                <div className="border-b border-gray-100 pb-3">
                  <label className="text-sm text-gray-500 block">Business Email</label>
                  <p className="font-medium break-words text-gray-800">{business_email || 'Not provided'}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500 block">Business Phone</label>
                  <p className="font-medium break-words text-gray-800">{business_phone || 'Not provided'}</p>
                </div>
              </div>
            </div>

            {/* Address Information Card */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="text-xl font-semibold mb-4 flex items-center">
                <FaMapMarkerAlt className="mr-2 text-[#004B95]" />
                Address
              </h2>
              <div className="space-y-4">
                <div className="border-b border-gray-100 pb-3">
                  <label className="text-sm text-gray-500 block">Street Address</label>
                  <p className="font-medium break-words text-gray-800">
                    {(flat_house_number || street_address) ? (
                      <>
                        {flat_house_number && `${flat_house_number}, `}
                        {street_address}
                      </>
                    ) : 'Not provided'}
                  </p>
                </div>
                <div className="border-b border-gray-100 pb-3">
                  <label className="text-sm text-gray-500 block">City & County</label>
                  <p className="font-medium break-words text-gray-800">
                    {city_town ? (
                      <>
                        {city_town}
                        {county && `, ${county}`}
                      </>
                    ) : 'Not provided'}
                  </p>
                </div>
                <div>
                  <label className="text-sm text-gray-500 block">Post Code & Country</label>
                  <p className="font-medium break-words text-gray-800">
                    {post_code ? (
                      <>
                        {post_code}
                        {country && `, ${country}`}
                      </>
                    ) : 'Not provided'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Description Card - Full Width */}
          <div className="bg-white rounded-xl shadow-sm p-6 mt-6">
            <h2 className="text-xl font-semibold mb-4 flex items-center">
              <FaInfoCircle className="mr-2 text-[#004B95]" />
              About
            </h2>
            <p className="text-gray-700 whitespace-pre-wrap">
              {description || 'No description provided. Click "Update Profile" to add a description about your organization.'}
            </p>
          </div>

          {/* Profile Update Modal */}
          <ProfileUpdateModal
            isOpen={isModalOpen}
            onRequestClose={handleCloseModal}
            initialValues={initialValuesFull}
          />
        </>
      )}
    </div>
  );
};

export default Profile;
